import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Dialog, Tabs, Toast } from 'antd-mobile';
import {
  DeleteOutline,
  EditSOutline,
  LeftOutline,
  RightOutline
} from 'antd-mobile-icons';
import SafeArea from '@/components/SafeArea';
import Avatar from '@/components/Avatar';
import EmptyState from '@/components/EmptyState';
import { deleteCustomer, getCustomerDetail, CustomerDetailResponse } from '@/api/customers';
import dayjs from 'dayjs';
import './styles.css';

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('from');
  const sourceCustomerId = searchParams.get('customerId');
  const requestedReturnTo = searchParams.get('returnTo');
  const statisticsFrom = searchParams.get('statisticsFrom') === 'settings' ? 'settings' : 'home';
  const listVillage = searchParams.get('village');
  const listFrom = searchParams.get('listFrom');
  const customerListParams = new URLSearchParams();
  if (listVillage) customerListParams.set('village', listVillage);
  if (listFrom === 'search' || listFrom === 'statistics') customerListParams.set('from', listFrom);
  if (listFrom === 'statistics') customerListParams.set('statisticsFrom', statisticsFrom);
  const customerListQuery = customerListParams.toString();
  const currentDetailQuery = searchParams.toString();
  const currentDetailPath = `/customer/${id}${currentDetailQuery ? `?${currentDetailQuery}` : ''}`;
  const backDestination = requestedReturnTo?.startsWith('/')
    ? requestedReturnTo
    : source === 'search'
      ? '/search'
      : source === 'statistics'
        ? `/statistics?from=${statisticsFrom}`
        : source === 'customers'
          ? `/customers${customerListQuery ? `?${customerListQuery}` : ''}`
          : source === 'customer' && sourceCustomerId
            ? `/customer/${sourceCustomerId}`
            : '/customers';
  const backLabel = source === 'search' ? '搜索' : source === 'statistics' ? '统计' : '客户';
  const [data, setData] = useState<CustomerDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(false);
    setNotFound(false);
    try {
      const res = await getCustomerDetail(Number(id));
      if (res.code === 0) setData(res.data);
    } catch (error: any) {
      console.error(error);
      if (error?.response?.status === 404) {
        setNotFound(true);
      } else {
        setLoadError(true);
        Toast.show({ content: '获取客户详情失败', icon: 'fail' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleDelete = async () => {
    const result = await Dialog.confirm({
      content: '确定删除该客户吗？相关销售记录也会一并删除；如有受保护的历史数据，系统将阻止本次删除。'
    });
    if (!result || !id) return;

    try {
      await deleteCustomer(Number(id));
      Toast.show({ content: '删除成功', icon: 'success' });
      navigate(backDestination, { replace: true });
    } catch (error: any) {
      console.error(error);
      Toast.show({ content: error?.response?.data?.message || '删除失败', icon: 'fail' });
    }
  };

  if (loading) {
    return (
      <div className="ios-page customer-detail-page">
        <div className="customer-detail-state-header">
          <DetailNav backLabel={backLabel} onBack={() => navigate(backDestination, { replace: true })} />
        </div>
        <div className="customer-detail-loading" aria-label="正在加载客户详情">
          <div className="ios-skeleton" />
          <div className="ios-skeleton" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ios-page customer-detail-page">
        <div className="customer-detail-state-header">
          <DetailNav backLabel={backLabel} onBack={() => navigate(backDestination, { replace: true })} />
        </div>
        <EmptyState
          title={notFound ? '客户不存在' : '客户详情加载失败'}
          description={loadError ? '请检查网络后重试' : undefined}
          action={loadError ? (
            <button type="button" className="ios-button ios-button-primary" onClick={() => void fetchData()}>
              重新加载
            </button>
          ) : undefined}
        />
      </div>
    );
  }

  const { customer, salesRecords, sameVillageCustomers } = data;

  return (
    <div className="ios-page customer-detail-page">
      <section className="customer-detail-hero">
        <DetailNav
          backLabel={backLabel}
          onBack={() => navigate(backDestination, { replace: true })}
          onEdit={() => navigate(`/customer/form/${customer.id}?returnTo=${encodeURIComponent(currentDetailPath)}`)}
          onMore={handleDelete}
        />

        <motion.div
          className="customer-detail-profile"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <Avatar
            src={customer.avatar}
            name={customer.name}
            size={84}
            style={{
              margin: '0 auto',
              border: '3px solid #fff',
              boxShadow: 'none'
            }}
          />
          <h1>{customer.name}</h1>
          <p className="customer-detail-contact">
            <span>{customer.village || '未填写村'}</span>
            {customer.phone ? (
              <>
                <span aria-hidden="true">·</span>
                <a href={`tel:${customer.phone}`} aria-label={`拨打 ${customer.phone}`}>
                  {customer.phone}
                </a>
              </>
            ) : null}
          </p>
        </motion.div>
      </section>

      <motion.section
        className="customer-detail-metrics"
        aria-label="客户购买统计"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.04 }}
      >
        <Metric label="购买次数" value={customer.buy_count} unit="次" />
        <Metric label="累计斤数" value={customer.total_weight} unit="斤" />
      </motion.section>

      <section className="customer-detail-tabs">
        <Tabs defaultActiveKey="sales">
          <Tabs.Tab title="购买" key="sales">
            {salesRecords.length === 0 ? (
              <EmptyState title="暂无购买记录" description="点击下方「记销售」添加" />
            ) : (
              <div className="customer-detail-timeline">
                {salesRecords.map((record: any, index: number) => (
                  <motion.div
                    key={record.id}
                    className="customer-detail-timeline-item"
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(index * 0.025, 0.12) }}
                  >
                    <button
                      type="button"
                      className="customer-detail-record"
                      aria-label={`修改 ${record.category_name} 销售记录`}
                      onClick={() =>
                        navigate(
                          `/sale/${record.id}/edit?from=customer&customerId=${customer.id}&returnTo=${encodeURIComponent(currentDetailPath)}`
                        )
                      }
                    >
                      <span className="customer-detail-record-title">
                        {record.category_name} · {record.price_level_name}
                      </span>
                      <RightOutline className="customer-detail-record-arrow" />
                      <time className="customer-detail-record-time">
                        {dayjs(record.sale_time).format('M月D日 HH:mm')}
                      </time>
                      <span className="customer-detail-record-quantity">
                        {record.weight} {record.unit || '斤'}
                      </span>
                      <strong className="customer-detail-record-value pro-number">
                        ¥{record.total_amount}
                      </strong>
                      {record.note && (
                        <span className="customer-detail-record-note">备注：{record.note}</span>
                      )}
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </Tabs.Tab>

          <Tabs.Tab title="同村" key="sameVillage">
            {sameVillageCustomers.length === 0 ? (
              <EmptyState title="暂无同村买家" />
            ) : (
              <div className="customer-same-village-list">
                {sameVillageCustomers.map((sameVillageCustomer, index) => (
                  <motion.button
                    type="button"
                    key={sameVillageCustomer.id}
                    className="customer-same-village-item"
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: Math.min(index * 0.025, 0.12) }}
                    onClick={() => navigate(`/customer/${sameVillageCustomer.id}?returnTo=${encodeURIComponent(currentDetailPath)}`)}
                    data-haptic="selection"
                  >
                    <Avatar
                      src={sameVillageCustomer.avatar}
                      name={sameVillageCustomer.name}
                      size={44}
                      style={{ boxShadow: 'none' }}
                    />
                    <span>{sameVillageCustomer.name}</span>
                  </motion.button>
                ))}
              </div>
            )}
          </Tabs.Tab>
        </Tabs>
      </section>

      <div className="customer-detail-actions">
        <motion.button
          type="button"
          className="customer-detail-action customer-detail-action--primary"
          onClick={() => navigate(`/sale?customerId=${customer.id}&returnTo=${encodeURIComponent(currentDetailPath)}`)}
          whileTap={{ scale: 0.98 }}
          data-haptic="medium"
        >
          记销售
        </motion.button>
      </div>
    </div>
  );
}

function DetailNav({
  backLabel,
  onBack,
  onEdit,
  onMore
}: {
  backLabel: string;
  onBack: () => void;
  onEdit?: () => void;
  onMore?: () => void;
}) {
  return (
    <SafeArea top={false} className="customer-detail-safe-area">
      <div className="customer-detail-nav">
        <button type="button" className="customer-detail-back" onClick={onBack} data-haptic="light">
          <LeftOutline aria-hidden="true" />
          <span>{backLabel}</span>
        </button>
        <strong>客户详情</strong>
        <div className="customer-detail-nav-actions">
          {onEdit && (
            <button type="button" onClick={onEdit} aria-label="编辑客户" data-haptic="selection">
              <EditSOutline />
            </button>
          )}
          {onMore && (
            <button type="button" onClick={onMore} aria-label="删除客户" data-haptic="medium">
              <DeleteOutline />
            </button>
          )}
        </div>
      </div>
    </SafeArea>
  );
}

function Metric({ label, value, unit }: { label: string; value: number; unit: string }) {
  return (
    <div className="customer-detail-metric">
      <span>{label}</span>
      <strong className="pro-number">
        {value}
        <small>{unit}</small>
      </strong>
    </div>
  );
}
